// src/components/Sidebar.jsx
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Tooltip,
  Divider,
  Typography,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import HistoryIcon from '@mui/icons-material/History';
import GroupIcon from '@mui/icons-material/Group';
import LockResetIcon from '@mui/icons-material/LockReset';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { jwtDecode } from 'jwt-decode';
import ChangePasswordItem from './ChangePasswordItem';

const drawerWidth = 248;
const collapsedWidth = 72;

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Project', icon: <AssignmentIcon />, path: '/sample-registration-list' },
  { label: 'Customer Details', icon: <PersonIcon />, path: '/customer-details-list' },
];

// Admin-only nav items
const adminNavItems = [
  { label: 'User Access', icon: <GroupIcon />, path: '/admin/users' },
  { label: 'Account Approvals', icon: <HowToRegIcon />, path: '/admin/approvals' },
  { label: 'Activity Logs', icon: <HistoryIcon />, path: '/admin/activity' },
];

// Shared styles for a nav button — accent lets us reuse for admin (green) items
const navButtonSx = (isActive, isCollapsed, accent) => ({
  position: 'relative',
  mx: isCollapsed ? 1 : 1.5,
  my: 0.25,
  borderRadius: 2,
  minHeight: 46,
  color: isActive ? '#ffffff' : 'rgba(255,255,255,0.90)',
  backgroundColor: isActive ? 'rgba(255,255,255,0.16)' : 'transparent',
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  px: isCollapsed ? 0 : 1.5,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    height: isActive ? 22 : 0,
    width: 3,
    borderRadius: 3,
    backgroundColor: accent,
    transition: 'height 0.25s ease',
  },
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.12)',
    color: '#ffffff',
  },
});

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const isCollapsed = !hovered;

  // Profile dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const token = localStorage.getItem('token');
  let userRole = null;
  let userName = null;
  let userEmail = null;
  try {
    if (token) {
      const decoded = jwtDecode(token);
      userRole = decoded.role;
      // fall back through common claim names so the greeting always resolves
      userName =
        decoded.name ||
        decoded.username ||
        decoded.fullName ||
        decoded.email ||
        'User';
      userEmail = decoded.email || '';
    }
  } catch (_) { }

  const isAdminUser = userRole === 'Admin' || userRole === 'Super admin';

  // Build up-to-two-letter initials from the name
  const initials = (userName || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    navigate('/');
  };

  return (
    <Drawer
      variant="permanent"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #34498b 0%, #34498b 100%)',
          color: '#fff',
          overflowX: 'hidden',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRight: 'none',
          boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Logo Section — logo only, no text */}
      <Toolbar
        sx={{
          justifyContent: 'center',
          px: isCollapsed ? 0 : 2,
          py: 1.5,
          minHeight: '68px !important',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/logo.png"
          alt="Logo"
          sx={{
            height: isCollapsed ? 38 : 44,
            width: isCollapsed ? 38 : 'auto',
            maxWidth: isCollapsed ? 38 : 170,
            flexShrink: 0,
            borderRadius: 1.5,
            backgroundColor: '#fff',
            padding: isCollapsed ? '4px' : '5px 10px',
            objectFit: 'contain',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease-in-out',
          }}
        />
      </Toolbar>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />

      {/* Main Navigation */}
      <Box sx={{ flexGrow: 1, mt: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>
        {!isCollapsed && (
          <Typography
            sx={{
              px: 3,
              mb: 0.5,
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 1.2,
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
            }}
          >
            Menu
          </Typography>
        )}
        <List sx={{ py: 0 }}>
          {navItems.map((item) => {
            const path = location.pathname.toLowerCase();
            let isActive = false;
            if (item.label === 'Project') {
              isActive =
                path.startsWith('/sample') ||
                path.startsWith('/library') ||
                path.startsWith('/sequencing') ||
                path.startsWith('/bio') ||
                path.startsWith('/project') ||
                path.startsWith('/customer-project');
            } else if (item.label === 'Customer Details') {
              isActive =
                path === '/customer-details-list' ||
                path === '/customerdetails' ||
                path.startsWith('/customer-projects');
            } else {
              isActive = path === item.path.toLowerCase();
            }
            return (
              <Tooltip key={item.label} title={isCollapsed ? item.label : ''} placement="right" arrow>
                <ListItemButton component={Link} to={item.path} sx={navButtonSx(isActive, isCollapsed, '#ffffff')}>
                  <ListItemIcon
                    sx={{ color: 'inherit', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center', '& svg': { fontSize: 21 } }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500, letterSpacing: 0.2 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>

        {/* Admin-only section */}
        {isAdminUser && (
          <>
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)', mx: 2, my: 1.5 }} />
            {!isCollapsed && (
              <Typography
                sx={{
                  px: 3,
                  mb: 0.5,
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 1.2,
                  color: 'rgba(255,255,255,0.6)',
                  textTransform: 'uppercase',
                }}
              >
                Administration
              </Typography>
            )}
            <List sx={{ py: 0 }}>
              {adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Tooltip key={item.label} title={isCollapsed ? item.label : ''} placement="right" arrow>
                    <ListItemButton component={Link} to={item.path} sx={navButtonSx(isActive, isCollapsed, '#86efac')}>
                      <ListItemIcon
                        sx={{ color: 'inherit', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center', '& svg': { fontSize: 21 } }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500, letterSpacing: 0.2 }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </>
        )}
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />

      {/* Footer: clickable profile that opens the account dropdown */}
      <Box sx={{ p: 1 }}>
        <Tooltip title={isCollapsed ? `Hi, ${userName}` : ''} placement="right" arrow>
          <ListItemButton
            onClick={openMenu}
            sx={{
              borderRadius: 2.5,
              px: isCollapsed ? 0 : 1.25,
              py: 1,
              gap: 1.25,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              backgroundColor: menuOpen ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
            }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  fontSize: 14,
                  fontWeight: 700,
                  bgcolor: '#ffffff',
                  color: '#34498b',
                }}
              >
                {initials}
              </Avatar>
              {/* Online status dot */}
              <Box
                sx={{
                  position: 'absolute',
                  right: -1,
                  bottom: -1,
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  border: '2px solid #34498b',
                }}
              />
            </Box>

            {!isCollapsed && (
              <>
                <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: '#fff',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    Hi, {userName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#4ade80' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
                      Online
                    </Typography>
                  </Box>
                </Box>
                <KeyboardArrowUpIcon
                  sx={{
                    fontSize: 20,
                    color: 'rgba(255,255,255,0.8)',
                    transition: 'transform 0.2s ease',
                    transform: menuOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                />
              </>
            )}
          </ListItemButton>
        </Tooltip>
      </Box>

      {/* Account dropdown card (matches reference) */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              ml: 1,
              minWidth: 230,
              borderRadius: 2.5,
              overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            },
          },
        }}
      >
        {/* Header: name + email */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: '#1f2937', lineHeight: 1.3 }}>
            {userName}
          </Typography>
          {userEmail && (
            <Typography sx={{ fontSize: 12.5, color: '#6b7280', mt: 0.25 }}>
              {userEmail}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Change Password — your existing component, styled as a menu row.
            Wrapping it lets clicking the row also close the menu. */}
        <Box onClick={closeMenu} sx={{ py: 0.5, '& .MuiListItemButton-root': { borderRadius: 0 } }}>
          <ChangePasswordItem collapsed={false} />
        </Box>

        <Divider />

        {/* Sign Out */}
        <MenuItem
          onClick={() => {
            closeMenu();
            handleLogout();
          }}
          sx={{
            py: 1.25,
            px: 2,
            color: '#dc2626',
            fontSize: 13.5,
            fontWeight: 600,
            '&:hover': { backgroundColor: 'rgba(220,38,38,0.06)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: '#dc2626' }}>
            <LogoutIcon sx={{ fontSize: 19 }} />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </Drawer>
  );
};

export default Sidebar;